import { useState } from 'react';
import Loading from '../components/Loading';
import FeeI from '../interfaces/Fee';
import FeeFormProps from '../interfaces/FeeFormProps';
import { SubmitHandler, useForm } from 'react-hook-form';
import useDiscountFee from '../hooks/useDiscountFee';
import { compact } from 'lodash';
import updateFee from '../functions/updateFee';
import Form from '../components/Form';

/**
 * All Access Late Check-In Surcharges admin form.
 *
 * Each OtherFee document represents a threshold (e.g. "After 7:00 PM
 * Surcharge") with an `afterHour` (hour of day, 0–23) and a `value`
 * (dollar amount). The admin can edit the dollar amount only — the
 * thresholds are fixed labels in business rules.
 *
 * The legacy "before X pm" picker has been removed; the surcharge
 * algorithm in PinkFeeService uses afterHour as the threshold and
 * picks the highest met threshold (non-stacked).
 */
const DiscountForm = ({ feeType, setAction }: FeeFormProps) => {
    const [loading, setLoading] = useState<boolean>(true);
    const { fees } = useDiscountFee(feeType, setLoading);
    const { register, handleSubmit } = useForm<Record<string, number>>();

    const handleSave: SubmitHandler<Record<string, number>> = async (data) => {
        if (!data) {
            setAction({ status: false, type: feeType });
            return;
        }
        const feesData = fees.map((fee) => {
            const v = (data as any)[fee.name];
            if (v === undefined) return null;
            return {
                name: fee.name,
                value: v,
            };
        });
        await updateFee(
            compact(feesData) as FeeI[],
            feeType,
            setLoading,
            setAction
        );
    };

    if (loading) {
        return <Loading loading={loading} />;
    }

    // Sort by afterHour so the rows render in ascending threshold order.
    const sorted = [...fees].sort(
        (a: any, b: any) => (a.afterHour || 0) - (b.afterHour || 0)
    );

    return (
        <>
            <div>
                <h3 className="edit-fee-container__text">
                    Set the dollar amount added on top of the All Access
                    base fee when a guest checks in at or after each
                    threshold. The highest threshold met wins (non-stacked).
                </h3>
            </div>

            <Form onSubmit={handleSubmit(handleSave)}>
                {sorted.map((fee: any) => (
                    <div key={fee._id} className="margin">
                        <span className="form-span">
                            {fee.name}
                        </span>
                        <input
                            {...register(fee.name, {
                                required: true,
                                setValueAs: (value) => parseInt(value, 10),
                            })}
                            className="form-input"
                            type="number"
                            min={0}
                            id={fee._id}
                            defaultValue={fee.value}
                        />
                    </div>
                ))}
                <button className="green-button margin">Save</button>
                <button
                    className="red-button margin"
                    onClick={() =>
                        setAction({
                            status: false,
                            type: feeType,
                        })
                    }
                >
                    Cancel
                </button>
            </Form>
        </>
    );
};

export default DiscountForm;
